/**
 * Zustand store.
 *
 * Wave A keeps the store tiny: authenticated user snapshot, preferred
 * theme, and the active deal filter on the list page. Everything else
 * stays in component state until Wave B proves it needs sharing.
 *
 * Do NOT put server responses in here long-term - Wave B will introduce
 * a lightweight cache layer (either TanStack Query or a hand-rolled
 * staleness map) for that.
 */

import { create } from 'zustand';

export interface UserSnapshot {
  id: string;
  telegramId: string; // serialized bigint
  username: string | null;
  tonAddress: string | null;
  reputationScore: number;
  role: 'user' | 'arbiter' | 'admin';
  isBanned: boolean;
}

export type DealFilter =
  | 'all'
  | 'active'
  | 'awaiting_funding'
  | 'awaiting_delivery'
  | 'disputed'
  | 'completed';

interface AppState {
  user: UserSnapshot | null;
  setUser: (user: UserSnapshot | null) => void;

  theme: 'dark' | 'light';
  setTheme: (theme: 'dark' | 'light') => void;

  dealFilter: DealFilter;
  setDealFilter: (filter: DealFilter) => void;

  reset: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  user: null,
  setUser: (user) => set({ user }),

  theme: 'dark',
  setTheme: (theme) => {
    document.documentElement.dataset.theme = theme;
    set({ theme });
  },

  dealFilter: 'all',
  setDealFilter: (dealFilter) => set({ dealFilter }),

  reset: () =>
    set({
      user: null,
      theme: 'dark',
      dealFilter: 'all',
    }),
}));
